import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    ArrowLeft, 
    AlertTriangle, 
    AlertCircle, 
    CheckCircle,
    Stethoscope,
    Pill,
    Heart,
    Search,
    Clock,
    FileText,
    Lightbulb,
    Activity,
    Phone,
    Share2,
    Copy,
    CheckCircle2,
    Mail,
    ExternalLink,
    Image as ImageIcon,
    Download,
    MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urgencyConfig = {
    EMERGENCY: { 
        icon: AlertTriangle, 
        class: 'bg-red-500',
        textClass: 'text-red-600',
        bgClass: 'bg-red-50 dark:bg-red-950/30',
        borderClass: 'border-red-200 dark:border-red-800',
        label: 'Emergency',
        description: 'Seek immediate medical attention'
    },
    URGENT: { 
        icon: AlertCircle, 
        class: 'bg-amber-500',
        textClass: 'text-amber-600',
        bgClass: 'bg-amber-50 dark:bg-amber-950/30',
        borderClass: 'border-amber-200 dark:border-amber-800',
        label: 'Urgent',
        description: 'See a doctor within 24-48 hours'
    },
    ROUTINE: { 
        icon: CheckCircle, 
        class: 'bg-emerald-500',
        textClass: 'text-emerald-600',
        bgClass: 'bg-emerald-50 dark:bg-emerald-950/30',
        borderClass: 'border-emerald-200 dark:border-emerald-800',
        label: 'Routine',
        description: 'Can wait for a scheduled appointment'
    }
};

export default function DiagnosisResult() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [diagnosis, setDiagnosis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareLink, setShareLink] = useState('');
    const [shareLoading, setShareLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchDiagnosis();
    }, [id]);

    const fetchDiagnosis = async () => {
        try {
            const response = await axios.get(`${API}/symptoms/${id}`);
            setDiagnosis(response.data);
        } catch (err) {
            setError('Failed to load diagnosis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async () => {
        setShareLoading(true);
        try {
            const response = await axios.post(`${API}/symptoms/${id}/share`);
            const fullUrl = `${window.location.origin}${response.data.share_url}`;
            setShareLink(fullUrl);
            setShareDialogOpen(true);
        } catch (err) {
            toast.error('Failed to generate share link');
        } finally {
            setShareLoading(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopied(true);
            toast.success('Link copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Failed to copy link');
        }
    };

    const shareViaEmail = () => {
        const subject = encodeURIComponent('My Health Assessment - MediGuide');
        const body = encodeURIComponent(`Hello,

I'd like to share my health assessment from MediGuide with you for review.

View my assessment here: ${shareLink}

This link is valid for 7 days.

Best regards`);
        window.open(`mailto:?subject=${subject}&body=${body}`);
    };

    const handleDownloadPdf = async () => {
        try {
            const response = await axios.get(`${API}/symptoms/${id}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `mediguide-report-${id.slice(0, 8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('PDF downloaded successfully!');
        } catch (err) {
            toast.error('Failed to download PDF');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('loadingDiagnosis') || 'Loading diagnosis...'}</p>
                </div>
            </div>
        );
    }

    if (error || !diagnosis) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Card className="max-w-md mx-4">
                    <CardContent className="pt-6 text-center">
                        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">{t('errorLoadingDiagnosis') || 'Error Loading Diagnosis'}</p>
                        <p className="text-muted-foreground mb-4">{error}</p>
                        <Button onClick={() => navigate('/dashboard')}>
                            {t('returnToDashboard') || 'Return to Dashboard'}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const config = urgencyConfig[diagnosis.urgency_level] || urgencyConfig.ROUTINE;
    const UrgencyIcon = config.icon;

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="diagnosis-result-page">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/dashboard')}
                        className="mb-4"
                        data-testid="back-to-dashboard"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t('backToDashboard') || 'Back to Dashboard'}
                    </Button>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                <Clock className="w-4 h-4" />
                                {formatDate(diagnosis.created_at)}
                                {diagnosis.has_image && (
                                    <Badge variant="outline" className="ml-2">
                                        <ImageIcon className="w-3 h-3 mr-1" />
                                        {t('imageAnalyzed') || 'Image Analyzed'}
                                    </Badge>
                                )}
                            </div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight">
                                {t('yourHealthAssessment') || 'Your Health Assessment'}
                            </h1>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button 
                                onClick={handleDownloadPdf}
                                variant="outline"
                                className="rounded-full"
                                data-testid="pdf-download-btn"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {t('downloadPDF') || 'Download PDF'}
                            </Button>
                            <Button 
                                onClick={handleShare}
                                variant="outline"
                                className="rounded-full"
                                disabled={shareLoading}
                                data-testid="share-btn"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                {shareLoading ? t('generating') || 'Generating...' : t('shareWithDoctor') || 'Share with Doctor'}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Urgency Banner */}
                <Card 
                    className={`mb-6 border-2 ${config.borderClass} ${config.bgClass} ${diagnosis.urgency_level === 'EMERGENCY' ? 'urgency-emergency-pulse' : ''}`}
                    data-testid="urgency-banner"
                >
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className={`w-14 h-14 rounded-2xl ${config.class} flex items-center justify-center flex-shrink-0`}>
                                <UrgencyIcon className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <h2 className={`font-heading text-2xl font-bold ${config.textClass}`}>
                                        {t(config.label.toLowerCase()) || config.label}
                                    </h2>
                                    <Badge className={config.class}>{diagnosis.urgency_level}</Badge>
                                </div>
                                <p className="text-lg">{diagnosis.urgency_description}</p>
                                
                                {diagnosis.urgency_level === 'EMERGENCY' && (
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <Button 
                                            className="bg-red-600 hover:bg-red-700 rounded-full"
                                            onClick={() => window.location.href = 'tel:911'}
                                            data-testid="emergency-call-btn"
                                        >
                                            <Phone className="w-4 h-4 mr-2" />
                                            {t('callEmergencyServices') || 'Call Emergency Services'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Symptoms Summary */}
                <Card className="mb-6" data-testid="symptoms-summary">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Activity className="w-5 h-5 text-primary" />
                            {t('reportedSymptoms') || 'Reported Symptoms'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {diagnosis.symptoms.symptoms.map((symptom, i) => (
                                <Badge key={i} variant="secondary" className="px-3 py-1">
                                    {symptom}
                                </Badge>
                            ))}
                        </div>
                        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="text-muted-foreground">{t('bodyParts') || 'Body Parts'}:</span>
                                <p className="font-medium">{diagnosis.symptoms.body_parts.join(', ')}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Duration:</span>
                                <p className="font-medium">{diagnosis.symptoms.duration}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Severity:</span>
                                <p className="font-medium capitalize">{diagnosis.symptoms.severity.replace('_', ' ')}</p>
                            </div>
                        </div>
                        
                        {/* Medical History Summary */}
                        {diagnosis.symptoms.medical_history && (
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <h4 className="text-sm font-medium mb-2">Medical History Considered:</h4>
                                <div className="flex flex-wrap gap-2 text-xs">
                                    {diagnosis.symptoms.medical_history.conditions?.length > 0 && (
                                        <Badge variant="outline">
                                            {diagnosis.symptoms.medical_history.conditions.length} conditions
                                        </Badge>
                                    )}
                                    {diagnosis.symptoms.medical_history.medications?.length > 0 && (
                                        <Badge variant="outline">
                                            {diagnosis.symptoms.medical_history.medications.length} medications
                                        </Badge>
                                    )}
                                    {diagnosis.symptoms.medical_history.allergies?.length > 0 && (
                                        <Badge variant="outline">
                                            {diagnosis.symptoms.medical_history.allergies.length} allergies
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Tabbed Content */}
                <Tabs defaultValue="conditions" className="space-y-6" data-testid="diagnosis-tabs">
                    <TabsList className="grid grid-cols-4 lg:w-auto lg:inline-grid">
                        <TabsTrigger value="conditions" className="gap-2">
                            <Stethoscope className="w-4 h-4 hidden sm:inline" />
                            Conditions
                        </TabsTrigger>
                        <TabsTrigger value="recommendations" className="gap-2">
                            <Lightbulb className="w-4 h-4 hidden sm:inline" />
                            Advice
                        </TabsTrigger>
                        <TabsTrigger value="treatment" className="gap-2">
                            <Pill className="w-4 h-4 hidden sm:inline" />
                            Treatment
                        </TabsTrigger>
                        <TabsTrigger value="investigations" className="gap-2">
                            <Search className="w-4 h-4 hidden sm:inline" />
                            Tests
                        </TabsTrigger>
                    </TabsList>

                    {/* Possible Conditions */}
                    <TabsContent value="conditions">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading">Possible Conditions</CardTitle>
                                <CardDescription>
                                    Based on your symptoms, these conditions may be relevant
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {diagnosis.possible_conditions.map((condition, i) => (
                                    <div 
                                        key={i} 
                                        className="p-4 rounded-xl border border-slate-200 dark:border-slate-700"
                                        data-testid={`condition-${i}`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <h4 className="font-semibold text-lg">{condition.name}</h4>
                                            <Badge variant={
                                                condition.likelihood === 'High' ? 'destructive' :
                                                condition.likelihood === 'Medium' ? 'default' : 'secondary'
                                            }>
                                                {condition.likelihood} likelihood
                                            </Badge>
                                        </div>
                                        <p className="text-muted-foreground">{condition.description}</p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Recommendations */}
                    <TabsContent value="recommendations">
                        <div className="grid gap-6">
                            {/* First Aid Tips */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading flex items-center gap-2">
                                        <Heart className="w-5 h-5 text-red-500" />
                                        First Aid Tips
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-3">
                                        {diagnosis.first_aid_tips.map((tip, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                                                <span>{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>

                            {/* Recommendations */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading">Recommendations</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                                        <h4 className="font-semibold text-primary mb-2">Immediate Action</h4>
                                        <p>{diagnosis.recommendations.immediate_action}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                                        <h4 className="font-semibold text-amber-700 dark:text-amber-400 mb-2">When to Seek Care</h4>
                                        <p>{diagnosis.recommendations.when_to_seek_care}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                                        <h4 className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2">Self-Care Tips</h4>
                                        <p>{diagnosis.recommendations.self_care}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Treatment Options */}
                    <TabsContent value="treatment">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Pill className="w-5 h-5 text-primary" />
                                    Treatment Options
                                </CardTitle>
                                <CardDescription>
                                    Possible treatment approaches (consult a healthcare provider)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3">
                                    {diagnosis.treatment_options.map((option, i) => (
                                        <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-xs font-bold text-primary">{i + 1}</span>
                                            </div>
                                            <span>{option}</span>
                                        </li>
                                    ))}
                                </ul>
                                
                                <div className="mt-6 p-4 rounded-xl bg-muted">
                                    <h4 className="font-semibold mb-2">Expected Outcomes</h4>
                                    <p className="text-muted-foreground">{diagnosis.expected_outcomes}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Investigations */}
                    <TabsContent value="investigations">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Search className="w-5 h-5 text-primary" />
                                    Recommended Investigations
                                </CardTitle>
                                <CardDescription>
                                    Tests that may help diagnose your condition
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {diagnosis.investigations.map((test, i) => (
                                        <div 
                                            key={i} 
                                            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
                                        >
                                            <FileText className="w-5 h-5 text-primary" />
                                            <span>{test}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Related Health Information */}
                <Card className="mt-6" data-testid="related-info">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            Related Health Information
                        </CardTitle>
                        <CardDescription>
                            Learn more about conditions that may be relevant to your symptoms
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {diagnosis.possible_conditions.slice(0, 4).map((condition, i) => (
                                <Link 
                                    key={i}
                                    to={`/health-library?search=${encodeURIComponent(condition.name)}`}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:bg-muted/50 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Stethoscope className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-sm">{condition.name}</p>
                                        <p className="text-xs text-muted-foreground">View patient information</p>
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                </Link>
                            ))}
                        </div>
                        
                        {/* External Resources for conditions not in our library */}
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                                Additional Resources
                            </p>
                            <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                                For more detailed information, visit trusted medical sources:
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {diagnosis.possible_conditions.slice(0, 2).map((condition, i) => (
                                    <a
                                        key={i}
                                        href={`https://www.nhs.uk/search/results?q=${encodeURIComponent(condition.name)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-full text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-slate-700 transition-colors border border-blue-200 dark:border-slate-600"
                                    >
                                        {condition.name}
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                ))}
                                <a
                                    href="https://www.mayoclinic.org/diseases-conditions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-full text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-slate-700 transition-colors border border-blue-200 dark:border-slate-600"
                                >
                                    Mayo Clinic
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        </div>
                        
                        <div className="mt-4 text-center">
                            <Link to="/health-library">
                                <Button variant="outline" className="rounded-full">
                                    <FileText className="w-4 h-4 mr-2" />
                                    Browse Health Library
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>

                {/* Disclaimer */}
                <Card className="mt-6 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20" data-testid="disclaimer">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Important Disclaimer</h4>
                                <p className="text-sm text-amber-700 dark:text-amber-400">
                                    {diagnosis.disclaimer}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/symptom-check">
                        <Button variant="outline" className="w-full sm:w-auto rounded-full" data-testid="new-check-btn">
                            Start New Check
                        </Button>
                    </Link>
                    <Button 
                        onClick={handleShare}
                        className="w-full sm:w-auto rounded-full"
                        disabled={shareLoading}
                        data-testid="share-bottom-btn"
                    >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share with Doctor
                    </Button>
                </div>
            </div>

            {/* Share Dialog */}
            <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="share-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-primary" />
                            Share with Your Doctor
                        </DialogTitle>
                        <DialogDescription>
                            Send this link to your healthcare provider. The link is valid for 7 days.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        <div className="flex items-center gap-2">
                            <Input
                                value={shareLink}
                                readOnly
                                className="flex-1"
                                data-testid="share-link-input"
                            />
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={copyToClipboard}
                                data-testid="copy-link-btn"
                            >
                                {copied ? (
                                    <CheckCircle2 className="w-4 h-4 text-success" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                            <Button 
                                variant="outline" 
                                className="w-full justify-start"
                                onClick={shareViaEmail}
                                data-testid="email-share-btn"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Share via Email
                            </Button>
                            <Button 
                                variant="outline" 
                                className="w-full justify-start"
                                onClick={() => window.open(shareLink, '_blank')}
                                data-testid="preview-share-btn"
                            >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Preview Shared Page
                            </Button>
                        </div>

                        <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                            <p className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-success" />
                                Your doctor can view this without creating an account
                            </p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
